import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { labAPI, patientsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TestTube, Plus, Trash2, FileCheck2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function LabPage() {
  const { user, hasPermission } = useAuth();
  const [tab, setTab] = useState('tests');
  const [tests, setTests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const canManageCatalog = hasPermission('lab.manage_catalog');
  const canOrder = hasPermission('lab.order');
  const canEnterResults = hasPermission('lab.results');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [t, o] = await Promise.all([labAPI.listTests(), labAPI.listOrders()]);
      setTests(t.data || []); setOrders(o.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load lab data'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!user?.is_super_admin && !hasPermission('lab.view')) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="lab-restricted">
        <Card className="max-w-lg w-full"><CardHeader><CardTitle>Access Restricted</CardTitle></CardHeader>
          <CardContent>You don't have permission to view Lab.</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="lab-page">
      <div className="flex items-center gap-2">
        <TestTube className="text-emerald-600" />
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Lab Management</h1>
          <p className="text-sm text-slate-500">Tests catalog · Orders · Results entry.</p>
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="tests" data-testid="lab-tab-tests">Tests Catalog ({tests.length})</TabsTrigger>
          <TabsTrigger value="orders" data-testid="lab-tab-orders">Orders ({orders.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="tests">
          <TestsTab tests={tests} canManage={canManageCatalog} onChange={fetchAll} loading={loading} />
        </TabsContent>
        <TabsContent value="orders">
          <OrdersTab orders={orders} tests={tests} canOrder={canOrder} canEnter={canEnterResults} onChange={fetchAll} loading={loading} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const TestsTab = ({ tests, canManage, onChange, loading }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(''); const [price, setPrice] = useState(''); const [unit, setUnit] = useState(''); const [refRange, setRefRange] = useState(''); const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error('Test name required'); return; }
    setSaving(true);
    try {
      await labAPI.createTest({ name, price: parseFloat(price) || 0, unit, reference_range: refRange, category });
      toast.success('Test added'); setName(''); setPrice(''); setUnit(''); setRefRange(''); setCategory(''); setOpen(false); onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete test "${t.name}"?`)) return;
    try { await labAPI.deleteTest(t.id); toast.success('Deleted'); onChange?.(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Tests Catalog</CardTitle>
        {canManage && <Button size="sm" onClick={() => setOpen(true)} data-testid="lab-add-test-btn"><Plus size={14} className="mr-1" /> Add Test</Button>}
      </CardHeader>
      <CardContent>
        {loading ? <div className="py-6 text-center text-sm text-slate-500">Loading…</div> :
          tests.length === 0 ? <div className="py-6 text-center text-sm text-slate-500" data-testid="lab-tests-empty">No tests in the catalog yet.</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 border-b">
                <tr><th className="text-left py-2 px-2">Name</th><th className="text-left py-2 px-2">Category</th><th className="text-left py-2 px-2">Unit</th><th className="text-left py-2 px-2">Reference Range</th><th className="text-right py-2 px-2">Price (₹)</th>{canManage && <th />}</tr>
              </thead>
              <tbody>
                {tests.map(t => (
                  <tr key={t.id} className="border-b hover:bg-slate-50" data-testid={`lab-test-row-${t.id}`}>
                    <td className="py-2 px-2 font-medium">{t.name}</td>
                    <td className="py-2 px-2 text-slate-500">{t.category || '—'}</td>
                    <td className="py-2 px-2">{t.unit || '—'}</td>
                    <td className="py-2 px-2 text-xs">{t.reference_range || '—'}</td>
                    <td className="py-2 px-2 text-right">{t.price || 0}</td>
                    {canManage && <td className="py-2 px-2 text-right"><Button size="icon" variant="ghost" onClick={() => remove(t)}><Trash2 size={14} className="text-rose-500" /></Button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="lab-add-test-dialog">
          <DialogHeader><DialogTitle>Add Lab Test</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Complete Blood Count" data-testid="lab-test-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Hematology" /></div>
              <div><Label>Price (₹)</Label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="lab-test-price" /></div>
              <div><Label>Unit</Label><Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="mg/dL" /></div>
              <div><Label>Reference Range</Label><Input value={refRange} onChange={(e) => setRefRange(e.target.value)} placeholder="4.5 - 5.5 M/µL" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lab-test-save-btn">{saving ? 'Saving…' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const OrdersTab = ({ orders, tests, canOrder, canEnter, onChange, loading }) => {
  const [open, setOpen] = useState(false);
  const [resultsOrder, setResultsOrder] = useState(null);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Lab Orders</CardTitle>
        {canOrder && <Button size="sm" onClick={() => setOpen(true)} data-testid="lab-new-order-btn"><Plus size={14} className="mr-1" /> New Order</Button>}
      </CardHeader>
      <CardContent>
        {loading ? <div className="py-6 text-center text-sm text-slate-500">Loading…</div> :
          orders.length === 0 ? <div className="py-6 text-center text-sm text-slate-500" data-testid="lab-orders-empty">No orders yet.</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 border-b">
                <tr><th className="text-left py-2 px-2">Order No</th><th className="text-left py-2 px-2">Patient</th><th className="text-left py-2 px-2">Tests</th><th className="text-left py-2 px-2">Ordered By</th><th className="text-left py-2 px-2">Status</th><th className="text-left py-2 px-2">Date</th>{canEnter && <th />}</tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} className="border-b hover:bg-slate-50" data-testid={`lab-order-row-${o.id}`}>
                    <td className="py-2 px-2 font-mono text-xs">{o.order_no}</td>
                    <td className="py-2 px-2">{o.patient_name || '—'}</td>
                    <td className="py-2 px-2 max-w-xs"><div className="flex flex-wrap gap-1">{(o.tests || []).map(t => <Badge key={t.id} variant="outline" className="text-xs">{t.name}</Badge>)}</div></td>
                    <td className="py-2 px-2 text-xs">{o.ordered_by_name || '—'}</td>
                    <td className="py-2 px-2"><Badge variant="outline" className={o.status === 'completed' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-amber-100 text-amber-700 border-amber-300'}>{o.status}</Badge></td>
                    <td className="py-2 px-2 text-xs">{o.created_at ? format(new Date(o.created_at), 'dd MMM yy HH:mm') : ''}</td>
                    {canEnter && <td className="py-2 px-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="outline" onClick={() => setResultsOrder(o)} data-testid={`lab-enter-results-${o.id}`}><FileCheck2 size={14} className="mr-1" /> {o.status === 'completed' ? 'View' : 'Enter Results'}</Button>
                        <Button size="icon" variant="ghost" onClick={() => window.open(`/lab/print/${o.id}`, '_blank')} title="Print Report" data-testid={`lab-print-${o.id}`}><Printer size={14} className="text-emerald-600" /></Button>
                      </div>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </CardContent>
      <NewOrderDialog open={open} onOpenChange={setOpen} tests={tests} onCreated={onChange} />
      <ResultsDialog order={resultsOrder} onOpenChange={(v) => !v && setResultsOrder(null)} onSaved={onChange} />
    </Card>
  );
};

const NewOrderDialog = ({ open, onOpenChange, tests, onCreated }) => {
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState('');
  const [selectedTests, setSelectedTests] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    patientsAPI.list({ limit: 500 }).then(r => setPatients(r.data || [])).catch(() => {});
    setPatientId(''); setSelectedTests([]);
  }, [open]);

  const toggle = (id) => setSelectedTests(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const submit = async () => {
    if (!patientId || selectedTests.length === 0) { toast.error('Select patient and at least one test'); return; }
    const p = patients.find(x => x.id === patientId);
    setSaving(true);
    try {
      await labAPI.createOrder({ patient_id: patientId, patient_name: p ? `${p.first_name} ${p.last_name}` : '', test_ids: selectedTests });
      toast.success('Order placed'); onCreated?.(); onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="lab-new-order-dialog">
        <DialogHeader><DialogTitle>New Lab Order</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Patient *</Label>
            <Select value={patientId} onValueChange={setPatientId}>
              <SelectTrigger data-testid="lab-order-patient"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>{patients.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} {p.uhid ? `(${p.uhid})` : ''}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tests *</Label>
            <div className="max-h-64 overflow-y-auto border rounded p-2 space-y-1">
              {tests.length === 0 ? <p className="text-xs text-slate-500">No tests in catalog — add them first.</p> :
                tests.map(t => (
                  <label key={t.id} className="flex items-center gap-2 hover:bg-slate-50 px-2 py-1 rounded cursor-pointer">
                    <input type="checkbox" checked={selectedTests.includes(t.id)} onChange={() => toggle(t.id)} data-testid={`lab-order-test-${t.id}`} />
                    <span className="text-sm flex-1">{t.name}</span>
                    <span className="text-xs text-slate-500">₹{t.price || 0}</span>
                  </label>
                ))
              }
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lab-order-submit-btn">{saving ? 'Saving…' : 'Place Order'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ResultsDialog = ({ order, onOpenChange, onSaved }) => {
  const [results, setResults] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setResults(order?.results || {}); }, [order]);
  if (!order) return null;
  const update = (testId, patch) => setResults({ ...results, [testId]: { ...(results[testId] || {}), ...patch } });
  const save = async () => {
    setSaving(true);
    try { await labAPI.enterResults(order.id, results); toast.success('Results saved'); onSaved?.(); onOpenChange(false); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!order} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lab-results-dialog">
        <DialogHeader><DialogTitle>Results — {order.order_no} · {order.patient_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {(order.tests || []).map(t => (
            <div key={t.id} className="p-3 bg-slate-50 rounded grid grid-cols-12 gap-2 items-end">
              <div className="col-span-12 md:col-span-4"><Label>{t.name}</Label><p className="text-xs text-slate-500">Ref: {t.reference_range || '—'}</p></div>
              <div className="col-span-4 md:col-span-2"><Label className="text-xs">Value</Label><Input value={results[t.id]?.value || ''} onChange={(e) => update(t.id, { value: e.target.value })} data-testid={`lab-result-value-${t.id}`} /></div>
              <div className="col-span-3 md:col-span-2"><Label className="text-xs">Unit</Label><Input value={results[t.id]?.unit || t.unit || ''} onChange={(e) => update(t.id, { unit: e.target.value })} /></div>
              <div className="col-span-5 md:col-span-2"><Label className="text-xs">Flag</Label>
                <Select value={results[t.id]?.flag || ''} onValueChange={(v) => update(t.id, { flag: v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="low">Low</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-12 md:col-span-2"><Label className="text-xs">Notes</Label><Input value={results[t.id]?.notes || ''} onChange={(e) => update(t.id, { notes: e.target.value })} /></div>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button><Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lab-results-save-btn">{saving ? 'Saving…' : 'Save Results'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
